import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import {
  Clock, Send, AlertTriangle, CheckCircle2, XCircle, Hourglass, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const STATUS = {
  pending:  { label: "Venter på tilbud", icon: Hourglass,     cls: "bg-amber-50 border-amber-300 text-amber-900" },
  offered:  { label: "Tilbud sendt",     icon: Sparkles,      cls: "bg-[#BD984D]/10 border-[#BD984D]/40 text-[#0C1824]" },
  accepted: { label: "Kunde har godtatt", icon: CheckCircle2, cls: "bg-cyan-50 border-cyan-300 text-cyan-900" },
  applied:  { label: "Aktivert",         icon: CheckCircle2,  cls: "bg-emerald-50 border-emerald-300 text-emerald-900" },
  declined: { label: "Avslått",          icon: XCircle,       cls: "bg-slate-50 border-slate-200 text-slate-700" },
};

function fmtDt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("nb-NO");
}

export default function AdminTimeChangeCard({ bookingId, onChanged }) {
  const { user } = useAuth();
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [offerDraft, setOfferDraft] = useState({}); // { reqId: {price, is_free, note} }

  const load = async () => {
    try {
      const { data } = await api.get(`/time-change-requests?booking_id=${bookingId}`);
      setReqs(data || []);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, [bookingId]);

  const draftFor = (r) => offerDraft[r.id] || {
    price_nok: r.offer_price_nok ?? "",
    is_free: !!r.offer_is_free,
    offer_note: r.offer_note || "",
  };
  const setDraftFor = (id, patch) => setOfferDraft((s) => ({
    ...s, [id]: { ...draftFor({ id, ...(s[id] || {}) }), ...patch },
  }));

  const sendOffer = async (r) => {
    const d = draftFor(r);
    if (!d.is_free && (d.price_nok === "" || d.price_nok === null)) {
      toast.error("Oppgi pris eller marker som gratis");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/time-change-requests/${r.id}/offer`, {
        price_nok: d.is_free ? 0 : parseInt(d.price_nok, 10) || 0,
        is_free: !!d.is_free,
        offer_note: d.offer_note || "",
      });
      toast.success("Tilbud sendt");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende tilbud");
    } finally { setBusy(false); }
  };

  const applyNow = async (r) => {
    if (!window.confirm(
      `Bekrefte at nye tider skal settes på bookingen?\n\n${r.requested_checkin_time ? "Innsjekk " + r.requested_checkin_time : ""}${r.requested_checkin_time && r.requested_checkout_time ? " · " : ""}${r.requested_checkout_time ? "Utsjekk " + r.requested_checkout_time : ""}`,
    )) return;
    setBusy(true);
    try {
      await api.post(`/time-change-requests/${r.id}/apply`);
      toast.success("Tidene er aktivert");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke anvende");
    } finally { setBusy(false); }
  };

  const adminDecline = async (r) => {
    const reason = window.prompt("Grunn til avslag (vises til kunden)");
    if (reason === null) return;
    setBusy(true);
    try {
      await api.post(`/time-change-requests/${r.id}/admin-decline`, { reason });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke avslå");
    } finally { setBusy(false); }
  };

  const active = reqs.filter((r) => r.status !== "declined" && r.status !== "applied");
  const closed = reqs.filter((r) => r.status === "declined" || r.status === "applied");

  if (reqs.length === 0) return null;

  return (
    <Card className="p-6" data-testid="admin-time-change-card">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-5 h-5 text-[#A57F39]" />
        <h2 className="text-lg font-semibold text-[#0C1824]">
          Kundens søknader om endrede tider
        </h2>
      </div>

      {active.length === 0 && (
        <p className="text-sm text-[#2B3F4D]">Ingen aktive søknader.</p>
      )}

      <div className="space-y-4">
        {active.map((r) => {
          const st = STATUS[r.status] || STATUS.pending;
          const Icon = st.icon;
          const d = draftFor(r);
          const offeredBySomeoneElse = r.status === "offered"
            && r.offered_by && r.offered_by !== user?.id;

          return (
            <div
              key={r.id}
              className={`rounded-lg border-2 p-4 ${st.cls}`}
              data-testid={`admin-time-change-item-${r.id}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="inline-flex items-center gap-2 font-semibold uppercase tracking-widest text-xs">
                  <Icon className="w-3.5 h-3.5" /> {st.label}
                </div>
                <div className="text-xs opacity-70">Mottatt {fmtDt(r.created_at)}</div>
              </div>

              <div className="mt-2 text-sm text-[#0C1824]">
                <strong>{r.customer_name}</strong> ber om:
                {r.requested_checkin_time && (
                  <span className="ml-1">innsjekk <b>{r.requested_checkin_time}</b></span>
                )}
                {r.requested_checkin_time && r.requested_checkout_time && ", "}
                {r.requested_checkout_time && (
                  <span>utsjekk <b>{r.requested_checkout_time}</b></span>
                )}
              </div>
              {r.customer_note && (
                <div className="text-xs italic mt-1 text-[#2B3F4D]">«{r.customer_note}»</div>
              )}

              {/* --- prominent notice if another admin already sent an offer --- */}
              {offeredBySomeoneElse && (
                <div
                  className="mt-3 rounded-md border-2 border-red-300 bg-red-50 p-3 flex items-start gap-2 text-sm text-red-900"
                  data-testid={`admin-time-change-already-offered-${r.id}`}
                >
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">
                      Tilbud allerede sendt av {r.offered_by_name} {fmtDt(r.offered_at)}
                    </div>
                    <div className="text-xs mt-0.5">
                      {r.offer_is_free ? "Gratis" : `${r.offer_price_nok || 0} kr`}
                      {r.offer_note ? ` – ${r.offer_note}` : ""}
                    </div>
                    <div className="text-xs mt-1">
                      Du kan justere tilbudet under, men avvent gjerne kundens svar først for å unngå dobbeltarbeid.
                    </div>
                  </div>
                </div>
              )}
              {r.status === "offered" && !offeredBySomeoneElse && (
                <div className="mt-2 text-xs text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Du sendte dette tilbudet {fmtDt(r.offered_at)}.
                </div>
              )}

              {/* --- offer form (pending / offered) --- */}
              {(r.status === "pending" || r.status === "offered") && (
                <div className="mt-4 rounded-md border border-white/60 bg-white/60 p-3 space-y-3">
                  <div className="text-xs uppercase tracking-widest text-[#A57F39] font-semibold">
                    Send / oppdater tilbud
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Pris (kr)</Label>
                      <Input
                        type="number" min={0}
                        value={d.is_free ? 0 : d.price_nok}
                        onChange={(e) => setDraftFor(r.id, { price_nok: e.target.value, is_free: false })}
                        disabled={d.is_free}
                        placeholder="300"
                        data-testid={`admin-time-change-price-${r.id}`}
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="inline-flex items-center gap-2 text-sm cursor-pointer h-10 px-2">
                        <Switch
                          checked={!!d.is_free}
                          onCheckedChange={(v) => setDraftFor(r.id, { is_free: v })}
                          data-testid={`admin-time-change-free-${r.id}`}
                        />
                        Gratis
                      </label>
                    </div>
                  </div>
                  <div>
                    <Label>Notat til kunden</Label>
                    <Textarea
                      rows={2}
                      value={d.offer_note}
                      onChange={(e) => setDraftFor(r.id, { offer_note: e.target.value })}
                      placeholder="F.eks. «Ok, henteperson er ledig kl. 12. Prisen dekker ekstra bemanning.»"
                      data-testid={`admin-time-change-note-${r.id}`}
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={() => sendOffer(r)}
                      disabled={busy}
                      className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                      data-testid={`admin-time-change-send-${r.id}`}
                    >
                      <Send className="w-4 h-4 mr-1.5" />
                      {r.status === "offered" ? "Oppdater tilbud" : "Send tilbud"}
                    </Button>
                    <Button
                      onClick={() => adminDecline(r)}
                      disabled={busy}
                      variant="outline"
                      data-testid={`admin-time-change-decline-${r.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-1.5" />
                      Avslå
                    </Button>
                  </div>
                </div>
              )}

              {/* --- accepted → apply now button --- */}
              {r.status === "accepted" && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  <div className="text-sm text-[#2B3F4D]">
                    Kunden godtok tilbudet
                    {r.offer_is_free ? " (gratis)" : ` (${r.offer_price_nok} kr)`}
                    {" "}– trykk «Anvend endring» når betalingen er ordnet og de nye tidene skal aktiveres.
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => applyNow(r)}
                      disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`admin-time-change-apply-${r.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1.5" />
                      Anvend endring
                    </Button>
                    <Button
                      onClick={() => adminDecline(r)}
                      disabled={busy}
                      variant="outline"
                      className="text-red-600"
                    >
                      Avslå likevel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {closed.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm font-medium text-[#A57F39]">
            Historikk ({closed.length})
          </summary>
          <div className="mt-3 space-y-2">
            {closed.map((r) => {
              const st = STATUS[r.status] || STATUS.declined;
              return (
                <div key={r.id} className="rounded border border-slate-200 p-3 text-sm text-[#2B3F4D]">
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-widest font-semibold">{st.label}</span>
                    <span className="text-xs opacity-70">{fmtDt(r.applied_at || r.declined_at || r.created_at)}</span>
                  </div>
                  <div className="text-xs mt-1">
                    {r.customer_name} · innsjekk {r.requested_checkin_time || "–"} · utsjekk {r.requested_checkout_time || "–"}
                    {r.status === "declined" && r.declined_reason ? ` · «${r.declined_reason}»` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </details>
      )}
    </Card>
  );
}
